"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { FaDatabase, FaFileExcel, FaFileWord, FaFileCsv, FaCog, FaCheck } from "react-icons/fa";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.css";
import "prismjs/components/prism-typescript";

export default function ExportDocs() {
	const t = useTranslations('docs.export');

	React.useEffect(() => {
		Prism.highlightAll();
	}, []);

	const exportFormatsExample = `
// Export formats available
export type ExportFormat = 'excel' | 'word' | 'csv';

// File naming convention
const getFileName = (type: ExportFormat) => {
  const extension = type === "excel" ? "xlsx" 
                  : type === "word" ? "docx" 
                  : "csv";
  return \`MTMT_\${author.name.replace(/\\s+/g, "_")}_\${formatDate()}.\${extension}\`;
};

// Example: MTMT_John_Doe_2024-01-15.xlsx`;

	const csvExportExample = `
// CSV Export implementation
const exportToCSV = () => {
  const selectedHeaders = headerSelections.csv;
  
  // Basic info section
  const basicData = getBasicData(author, stats, meta);
  const metaRows = [
    'Basic Information',
    \`Name,"\${basicData.name}"\`,
    \`MTMT ID,\${basicData.mtmtId}\`,
    \`Last Update,\${basicData.lastUpdate}\`,
    \`IFList version,\${basicData.ifListVersionYear ?? ''}\`,
    \`NorwayList version,\${basicData.norwayListVersionYear ?? ''}\`,
    ''
  ];
  
  // Header row
  const headerLabels = selectedHeaders.map(key => 
    defaultHeaders.find(h => h.key === key)?.label || key
  );
  
  let csvContent = metaRows.join("\\n") + "\\n" + headerLabels.join(",") + "\\n";
  
  // Data rows
  publications.forEach(pub => {
    const row = selectedHeaders.map(header => {
      const value = getPublicationData(pub, header, author.mtid);
      return typeof value === 'string' 
        ? \`"\${value.replace(/"/g, '""')}"\` 
        : value;
    }).join(",");
    csvContent += row + "\\n";
  });
  
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, getFileName("csv"));
};`;

	const headerSelectionExample = `
// Header selection and persistence
const defaultHeaders: ExportHeader[] = [
  { key: 'year', label: 'Year' },
  { key: 'title', label: 'Title' },
  { key: 'authors', label: 'Authors' },
  { key: 'journalBook', label: 'Journal/Book' },
  { key: 'type', label: 'Type' },
  { key: 'rating', label: 'Rating' },
  { key: 'if', label: 'Impact Factor' },
  { key: 'qScore', label: 'Q Score' },
  { key: 'iScore', label: 'I Score' },
  // ... more headers
];

// Persist selections in localStorage
const [headerSelections, setHeaderSelections] = useState(() => {
  const saved = localStorage.getItem('export-header-selections');
  return saved ? JSON.parse(saved) : {
    excel: defaultHeaders.map(h => h.key),
    word: defaultHeaders.map(h => h.key),
    csv: defaultHeaders.map(h => h.key),
  };
});

useEffect(() => {
  localStorage.setItem('export-header-selections', JSON.stringify(headerSelections));
}, [headerSelections]);`;

	const basicDataExample = `
// Basic data structure for export
interface BasicData {
  name: string;
  mtmtId: string;
  homepage: string;
  mtmtProfile: string;
  lastUpdate: string;
  ifListVersionYear: number | null;
  norwayListVersionYear: number | null;
  totalStats: {
    publications: number;
    scientificCitations: number;
    hIndex: number;
    iScore: number;
    iScoreWOS: number;
  };
  impactFactorStats: {
    total: number;
    relative: number;
    publicationsWithIF: number;
  };
  qScores: {
    total: number;
    fromArticles: number;
    fromBooks: number;
  };
}`;

	const publicationDataMappingExample = `
// Publication data extraction
const getPublicationData = (pub: Publication, header: string, mtmtId: string) => {
  const dataMap: { [key: string]: any } = {
    year: pub.publishedYear,
    title: pub.title,
    authors: pub.authorships?.map(a => 
      a.author?.label || \`\${a.familyName} \${a.givenName}\`
    ).join(", "),
    journalBook: pub.journal?.label || pub.book?.title || "",
    type: pub.getPublicationType(translations),
    scientific: pub.isScientific() ? 'Yes' : 'No',
    rating: pub.getRating(""),
    if: pub.IFindex || "",
    qScore: pub.qscore?.toFixed(3) || "",
    iScore: pub.iscore || "",
    citations: pub.citationCount || 0,
    scientificCitations: pub.scientificCitationCount || 0,
    share: pub.getAuthorShare(mtmtId).toFixed(3),
    url: pub.identifiers?.[0]?.realUrl || "",
    mtmtId: pub.mtid || "",
    hasISBNISSN: pub.hasISBNISSN === false ? 'No' : 'Yes',
    norwayLevel: typeof pub.norwayLevel === 'number' ? pub.norwayLevel : "",
  };
  return dataMap[header];
};`;

	return (
			<div className="prose prose-slate max-w-none prose-sm">
				<h1 className="flex items-center gap-3 text-2xl font-bold mb-4">
					<FaDatabase className="text-bme-main" />
					{t('title')}
				</h1>

				<p className="text-base text-gray-600 mb-6">
					{t('subtitle')}
				</p>

				{/* Export Formats Section */}
				<section id="formats" className="scroll-mt-20">
					<h2 className="text-3xl font-bold mt-12 mb-6 flex items-center gap-3">
						<FaFileExcel className="text-bme-main" />
						{t('formats.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('formats.description')}
					</p>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
						<div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
							<div className="flex items-center gap-3 mb-3">
								<FaFileExcel className="text-blue-600 text-2xl" />
								<h3 className="text-lg font-semibold">{t('formats.excel.title')}</h3>
							</div>
							<ul className="list-disc pl-6 space-y-1 text-sm">
								{[0, 1, 2, 3, 4].map((index) => {
									const feature = t(`formats.excel.feature${index}` as any)
									return feature ? <li key={index}>{feature}</li> : null
								})}
							</ul>
						</div>

						<div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
							<div className="flex items-center gap-3 mb-3">
								<FaFileWord className="text-green-600 text-2xl" />
								<h3 className="text-lg font-semibold">{t('formats.word.title')}</h3>
							</div>
							<ul className="list-disc pl-6 space-y-1 text-sm">
								{[0, 1, 2, 3, 4].map((index) => {
									const feature = t(`formats.word.feature${index}` as any)
									return feature ? <li key={index}>{feature}</li> : null
								})}
							</ul>
						</div>

						<div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg">
							<div className="flex items-center gap-3 mb-3">
								<FaFileCsv className="text-orange-600 text-2xl" />
								<h3 className="text-lg font-semibold">{t('formats.csv.title')}</h3>
							</div>
							<ul className="list-disc pl-6 space-y-1 text-sm">
								{[0, 1, 2, 3, 4].map((index) => {
									const feature = t(`formats.csv.feature${index}` as any)
									return feature ? <li key={index}>{feature}</li> : null
								})}
							</ul>
						</div>
					</div>

					<pre className="mt-4!">
						<code className="language-typescript">{exportFormatsExample}</code>
					</pre>
				</section>

				{/* File Naming Section */}
				<section id="file-naming" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('fileNaming.title')}</h2>

					<p className="text-lg mb-4">
						{t('fileNaming.description')}
					</p>

					<div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
						<h4 className="font-semibold mb-2">{t('fileNaming.format')}</h4>
						<code className="text-lg">{t('fileNaming.pattern')}</code>
					</div>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('fileNaming.examples.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><code>{t('fileNaming.examples.ex1')}</code></li>
						<li><code>{t('fileNaming.examples.ex2')}</code></li>
						<li><code>{t('fileNaming.examples.ex3')}</code></li>
					</ul>

					<div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
						<p className="text-blue-800 m-0">
							<strong>{t('fileNaming.note')}</strong>
						</p>
					</div>
				</section>

				{/* Header Selection Section */}
				<section id="header-selection" className="scroll-mt-20">
					<h2 className="text-3xl font-bold mt-12 mb-6 flex items-center gap-3">
						<FaCog className="text-bme-main" />
						{t('headers.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('headers.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('headers.available.title')}</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
						<ul className="list-disc pl-6 space-y-2">
							<li><strong>{t('headers.available.year')}</strong></li>
							<li><strong>{t('headers.available.titleField')}</strong></li>
							<li><strong>{t('headers.available.authors')}</strong></li>
							<li><strong>{t('headers.available.journalBook')}</strong></li>
							<li><strong>{t('headers.available.type')}</strong></li>
							<li><strong>{t('headers.available.scientific')}</strong></li>
							<li><strong>{t('headers.available.rating')}</strong></li>
							<li><strong>{t('headers.available.if')}</strong></li>
						</ul>
						<ul className="list-disc pl-6 space-y-2">
							<li><strong>{t('headers.available.qScore')}</strong></li>
							<li><strong>{t('headers.available.iScore')}</strong></li>
							<li><strong>{t('headers.available.citations')}</strong></li>
							<li><strong>{t('headers.available.scientificCitations')}</strong></li>
							<li><strong>{t('headers.available.share')}</strong></li>
							<li><strong>{t('headers.available.url')}</strong></li>
							<li><strong>{t('headers.available.mtmtId')}</strong></li>
							<li><strong>{t('headers.available.norwayLevel')}</strong></li>
							<li><strong>{t('headers.available.hasISBNISSN')}</strong></li>
						</ul>
					</div>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('headers.persistence.title')}</h3>
					<p className="mb-4">
						{t('headers.persistence.description')}
					</p>

					<pre className="mt-4!">
						<code className="language-typescript">{headerSelectionExample}</code>
					</pre>
				</section>

				{/* Basic Information Section */}
				<section id="basic-info" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('basicInfo.title')}</h2>

					<p className="text-lg mb-4">
						{t('basicInfo.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('basicInfo.author.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('basicInfo.author.name')}</strong></li>
						<li><strong>{t('basicInfo.author.mtmtId')}</strong></li>
						<li><strong>{t('basicInfo.author.lastUpdate')}</strong></li>
						<li><strong>{t('basicInfo.author.ifListVersion')}</strong></li>
						<li><strong>{t('basicInfo.author.norwayListVersion')}</strong></li>
					</ul>
					<div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-6">
						<p className="text-red-800 m-0">
							<strong>{t('norwayList.description')}</strong>
						</p>
					</div>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('basicInfo.statistics.title')}</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
						<div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
							<h4 className="font-semibold mb-2">{t('basicInfo.statistics.total.title')}</h4>
							<ul className="list-disc pl-6 space-y-1 text-sm">
								{[0, 1, 2, 3, 4].map((index) => {
									const item = t(`basicInfo.statistics.total.item${index}` as any)
									return item ? <li key={index}>{item}</li> : null
								})}
							</ul>
						</div>

						<div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
							<h4 className="font-semibold mb-2">{t('basicInfo.statistics.impactFactor.title')}</h4>
							<ul className="list-disc pl-6 space-y-1 text-sm">
								{[0, 1, 2].map((index) => {
									const item = t(`basicInfo.statistics.impactFactor.item${index}` as any)
									return item ? <li key={index}>{item}</li> : null
								})}
							</ul>
						</div>

						<div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
							<h4 className="font-semibold mb-2">{t('basicInfo.statistics.qScore.title')}</h4>
							<ul className="list-disc pl-6 space-y-1 text-sm">
								{[0, 1, 2].map((index) => {
									const item = t(`basicInfo.statistics.qScore.item${index}` as any)
									return item ? <li key={index}>{item}</li> : null
								})}
							</ul>
						</div>
					</div>

					<pre className="mt-4!">
						<code className="language-typescript">{basicDataExample}</code>
					</pre>
				</section>

				{/* Publication Data Section */}
				<section id="publication-data" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('publicationData.title')}</h2>

					<p className="text-lg mb-4">
						{t('publicationData.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('publicationData.extraction.title')}</h3>
					<p className="mb-4">
						{t('publicationData.extraction.description')}
					</p>

					<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
						<p className="text-yellow-800 m-0">
							<strong>{t('publicationData.extraction.note')}</strong>
						</p>
					</div>

					<pre className="mt-4!">
						<code className="language-typescript">{publicationDataMappingExample}</code>
					</pre>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('publicationData.formatting.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('publicationData.formatting.numeric')}</strong></li>
						<li><strong>{t('publicationData.formatting.boolean')}</strong></li>
						<li><strong>{t('publicationData.formatting.empty')}</strong></li>
						<li><strong>{t('publicationData.formatting.csvEscaping')}</strong></li>
					</ul>
				</section>

				{/* Excel-Specific Features Section */}
				<section id="excel-features" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('excel.title')}</h2>

					<p className="text-lg mb-4">
						{t('excel.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('excel.structure.title')}</h3>
					<ol className="list-decimal pl-6 mb-4 space-y-2">
						<li>{t('excel.structure.step1')}</li>
						<li>{t('excel.structure.step2')}</li>
					</ol>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('excel.styling.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('excel.styling.colorCoded')}</strong></li>
						<li><strong>{t('excel.styling.boldHeaders')}</strong></li>
						<li><strong>{t('excel.styling.hyperlinks')}</strong></li>
						<li><strong>{t('excel.styling.sectionHeaders')}</strong></li>
						<li><strong>{t('excel.styling.autoWidth')}</strong></li>
					</ul>

					<div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
						<p className="text-blue-800 m-0">
							<strong>{t('excel.library')}</strong>
						</p>
					</div>
				</section>

				{/* Word-Specific Features Section */}
				<section id="word-features" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('word.title')}</h2>

					<p className="text-lg mb-4">
						{t('word.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('word.structure.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('word.structure.titleSection')}</strong></li>
						<li><strong>{t('word.structure.statisticsTables')}</strong></li>
						<li><strong>{t('word.structure.publicationList')}</strong></li>
					</ul>

					<div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg mb-6">
						<p className="text-green-800 m-0">
							<strong>{t('word.library')}</strong>
						</p>
					</div>
				</section>

				{/* CSV-Specific Features Section */}
				<section id="csv-features" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('csv.title')}</h2>

					<p className="text-lg mb-4">
						{t('csv.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('csv.format.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('csv.format.commaSeparated')}</strong></li>
						<li><strong>{t('csv.format.quotedStrings')}</strong></li>
						<li><strong>{t('csv.format.escapedQuotes')}</strong></li>
						<li><strong>{t('csv.format.utf8')}</strong></li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('csv.structure.title')}</h3>
					<ol className="list-decimal pl-6 mb-4 space-y-2">
						<li>{t('csv.structure.step1')}</li>
						<li>{t('csv.structure.step2')}</li>
						<li>{t('csv.structure.step3')}</li>
						<li>{t('csv.structure.step4')}</li>
					</ol>

					<pre className="mt-4!">
						<code className="language-typescript">{csvExportExample}</code>
					</pre>
				</section>

				{/* Usage Guidelines Section */}
				<section id="usage" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('usage.title')}</h2>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('usage.bestPractices.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li className="flex items-start gap-2">
							<FaCheck className="text-green-500 mt-1 shrink-0" />
							<strong>{t('usage.bestPractices.selectHeaders')}</strong>
						</li>
						<li className="flex items-start gap-2">
							<FaCheck className="text-green-500 mt-1 shrink-0" />
							<strong>{t('usage.bestPractices.useExcel')}</strong>
						</li>
						<li className="flex items-start gap-2">
							<FaCheck className="text-green-500 mt-1 shrink-0" />
							<strong>{t('usage.bestPractices.useCSV')}</strong>
						</li>
						<li className="flex items-start gap-2">
							<FaCheck className="text-green-500 mt-1 shrink-0" />
							<strong>{t('usage.bestPractices.useWord')}</strong>
						</li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('usage.fileSize.title')}</h3>
					<p className="mb-4">
						{t('usage.fileSize.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>{t('usage.fileSize.reduceHeaders')}</li>
						<li>{t('usage.fileSize.useCSV')}</li>
						<li>{t('usage.fileSize.excelWord')}</li>
					</ul>
				</section>
			</div>
	);
}

